/**
 * MyPromotions — 我的贡献 page at /my-promotions. Shows the current
 * user's in-flight promotion requests.
 *
 * Reads via /api/school/my-promotions (school-backend proxy that
 * stamps X-User-Id from the validated Bearer before forwarding to
 * promotion-svc). Direct calls to /api/admin/promotions/mine from the
 * school-ui would 401 because promotion-svc is gateway-trust auth and
 * the school-bosong nginx vhost doesn't inject those headers.
 *
 * Withdraw + PromoteToOrgButton (the write paths) are temporarily
 * hidden — they hit promotion-svc directly and would also 401.
 * Re-enabling them needs either a Bearer-aware promotion-svc upstream
 * or matching backend proxies. Tracked as a follow-up; for now the
 * list-only view solves the "Failed to load" bug the user reported.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { authHeaders } from "../../api/school";

type Status = "pending" | "approved" | "rejected" | "withdrawn";

interface PromotionRequest {
  id: string;
  source_collection: string;
  target_collection: string;
  requested_by: string | null;
  requested_at: string;
  status: Status;
  reviewed_by: string | null;
  reviewed_at: string | null;
  decision_notes: string | null;
}

async function resubmitPromotion(id: string): Promise<void> {
  const r = await fetch(`/api/school/promotions/${id}/resubmit`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(
      typeof body.detail === "object" ? JSON.stringify(body.detail) : (body.detail ?? `HTTP ${r.status}`),
    );
  }
}

async function fetchMyPromotions(): Promise<PromotionRequest[]> {
  const r = await fetch("/api/school/my-promotions", { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const body = (await r.json()) as { requests?: PromotionRequest[] };
  return body.requests ?? [];
}

const STATUS_TONES: Record<Status, string> = {
  pending: "bg-sky-100 text-sky-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  withdrawn: "bg-slate-100 text-slate-600",
};

export function MyPromotions() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  async function resubmit(id: string) {
    try {
      await resubmitPromotion(id);
      void queryClient.invalidateQueries({ queryKey: ["my-promotions"] });
    } catch (e) {
      window.alert((e as Error).message);
    }
  }
  const { data: requests = [], isLoading, error } = useQuery({
    queryKey: ["my-promotions"],
    queryFn: fetchMyPromotions,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4 max-w-3xl p-6">
      <header>
        <h1 className="text-lg font-semibold text-slate-800">{t("myPromotions.title")}</h1>
        <p className="text-xs text-slate-500 mt-1">
          {t("myPromotions.subtitle")}
        </p>
      </header>

      <section className="rounded border border-slate-200 bg-white p-4">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">{t("myPromotions.requestsHeading")}</h2>
          <span className="text-xs text-slate-500">
            {t("myPromotions.countLabel", { count: requests.length })}
          </span>
        </header>

        {error ? (
          <p className="text-red-600 text-sm">
            {t("myPromotions.loadError", { msg: (error as Error).message })}
          </p>
        ) : isLoading ? (
          <p className="text-slate-500 text-sm italic">{t("myPromotions.loading")}</p>
        ) : requests.length === 0 ? (
          <p className="text-slate-500 text-xs italic">
            {t("myPromotions.empty")}
          </p>
        ) : (
          <ul className="space-y-2">
            {requests.map((r) => (
              <li key={r.id} className="rounded border border-slate-100 px-3 py-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-sky-700 truncate">{r.source_collection}</code>
                  <span className="text-slate-400">→</span>
                  <code className="text-sky-700 truncate">{r.target_collection}</code>
                  <StatusPill status={r.status} />
                </div>
                <div className="mt-1 text-[10px] text-slate-500">
                  {t("myPromotions.submittedAt", {
                    date: r.requested_at.slice(0, 16).replace("T", " "),
                  })}
                </div>
                {r.status !== "pending" && r.decision_notes && (
                  <div className="mt-1 rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-700">
                    <span className="text-slate-500">
                      {r.status === "approved"
                        ? t("myPromotions.approveNotePrefix")
                        : t("myPromotions.rejectNotePrefix")}
                    </span>
                    {r.decision_notes}
                  </div>
                )}
                {r.status === "rejected" && (
                  <button
                    onClick={() => void resubmit(r.id)}
                    className="mt-1.5 text-[11px] px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    {t("myPromotions.resubmit")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  const { t } = useTranslation();
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_TONES[status]}`}>
      {t(`myPromotions.status.${status}`)}
    </span>
  );
}
